import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { FilterBar } from '@/components/common/FilterBar';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { Tabs } from '@/components/common/Tabs';
import { Select2 } from '@/components/forms/Select2';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { marketDataIntegrationApi } from '@/api/services/marketDataIntegration';
import type {
  TenantCacheRowDto,
  TenantIntegrationRowDto,
} from '@/api/types/marketDataIntegration';
import { ApiError } from '@/api/client/abpError';
import { MarketDataIntegrationPermissions } from './permissions';
import { useRunIntegration, useStopIntegration, useTenants } from './api';

type Tab = 'cache' | 'integration';

type ActionTarget =
  | { kind: 'run'; row: TenantIntegrationRowDto }
  | { kind: 'stop'; row: TenantIntegrationRowDto }
  | null;

function formatUpdatedAt(value?: string | null) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'Stopped'
      ? 'bg-danger'
      : status === 'Running'
        ? 'bg-primary'
        : 'bg-info';
  return <span className={`badge ${cls}`}>{status}</span>;
}

export function MarketDataIntegrationPage() {
  const can = usePermission();
  const canRun = can(MarketDataIntegrationPermissions.run);
  const canStop = can(MarketDataIntegrationPermissions.stop);
  const canExport = can(MarketDataIntegrationPermissions.export);

  const toast = useToast();
  const tenantsQuery = useTenants();
  const runMut = useRunIntegration();
  const stopMut = useStopIntegration();

  const [tab, setTab] = useState<Tab>('cache');
  const [tenantId, setTenantId] = useState<string>('');
  const [actionTarget, setActionTarget] = useState<ActionTarget>(null);

  const extraParams = useMemo(
    () => (tenantId ? { tenantId } : {}),
    [tenantId],
  );

  const cacheTable = useServerDataTable<TenantCacheRowDto, { tenantId?: string }>({
    queryKey: ['marketDataIntegration', 'cache'],
    fetcher: (req) => marketDataIntegrationApi.getCacheTable(req),
    extraParams,
    enabled: tab === 'cache',
  });

  const integrationTable = useServerDataTable<TenantIntegrationRowDto, { tenantId?: string }>({
    queryKey: ['marketDataIntegration', 'integration'],
    fetcher: (req) => marketDataIntegrationApi.getIntegrationTable(req),
    extraParams,
    enabled: tab === 'integration',
  });

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function confirmAction() {
    if (!actionTarget) return;
    const { kind, row } = actionTarget;
    const mut = kind === 'run' ? runMut : stopMut;
    mut.mutate(
      { tenantId: row.tenantId },
      {
        onSuccess: (res) => {
          toast.success(res.message || (kind === 'run' ? 'Integration started.' : 'Integration stopped.'));
          setActionTarget(null);
        },
        onError: (err) => {
          reportError(err, kind === 'run' ? 'Failed to run integration.' : 'Failed to stop integration.');
          setActionTarget(null);
        },
      },
    );
  }

  const cacheColumns: DataGridColumn<TenantCacheRowDto>[] = [
    { key: 'tenantName', header: 'Client Name', sortable: true, cell: (r) => r.tenantName },
    { key: 'cacheName', header: 'Cache Name', sortable: true, cell: (r) => r.cacheName },
    { key: 'updatedAt', header: 'Updated At', sortable: true, cell: (r) => formatUpdatedAt(r.updatedAt) },
    { key: 'formattedSize', header: 'Data Size', sortable: true, cell: (r) => r.formattedSize },
  ];

  const integrationColumns: DataGridColumn<TenantIntegrationRowDto>[] = [
    { key: 'tenantName', header: 'Client Name', sortable: true, cell: (r) => r.tenantName },
    { key: 'status', header: 'Status', sortable: true, cell: (r) => <StatusBadge status={r.status} /> },
  ];

  const tenantOptions = tenantsQuery.data ?? [];

  return (
    <div>
      <PageHeader title="Market Data" breadcrumb={['Home', 'Market Data']} />

      {/* Tenant filter above the tabs (legacy layout). */}
      <FilterBar
        fields={[
          {
            node: (
              <Select2
                label="Tenant"
                placeholder="Select a client"
                value={tenantId}
                onChange={(v) => setTenantId(v ?? '')}
                options={tenantOptions.map((t) => ({ value: t.id, label: t.name }))}
              />
            ),
            width: 200,
          },
        ]}
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => {
              cacheTable.refetch();
              integrationTable.refetch();
            }}
          >
            Refresh All
          </button>
        }
      />

      <div className="card custom-card mb-2">
        <div className="card-header p-0">
          <Tabs
            tabs={[
              { key: 'cache', title: 'Market Data' },
              { key: 'integration', title: 'Integration' },
            ]}
            active={tab}
            onChange={(k) => setTab(k as Tab)}
          />
        </div>
        <div className="card-body">
          <div>
            {tab === 'cache' ? (
              <DataGrid
                controller={cacheTable}
                columns={cacheColumns}
                rowKey={(r) => `${r.tenantId}-${r.cacheKey}`}
                emptyMessage="No cached data."
                rowActions={
                  canExport
                    ? (row) =>
                        row.reachable && row.sizeBytes > 0 ? (
                          <a
                            className="btn btn-sm btn-primary"
                            title="Download Cached Data"
                            href={marketDataIntegrationApi.exportUrl(row.tenantId, row.cacheKey)}
                          >
                            <i className="fa fa-download me-1" />
                            Check Data
                          </a>
                        ) : null
                    : undefined
                }
              />
            ) : (
              <DataGrid
                controller={integrationTable}
                columns={integrationColumns}
                rowKey={(r) => r.tenantId}
                emptyMessage="No integrations."
                rowActions={
                  canRun || canStop
                    ? (row) => (
                        <div className="d-inline-flex gap-1 justify-content-center">
                          {canRun && row.status !== 'Running' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              title="Run Integration"
                              onClick={() => setActionTarget({ kind: 'run', row })}
                            >
                              <i className="fa fa-play me-1" />
                              Run
                            </button>
                          )}
                          {canStop && row.status === 'Running' && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Stop Integration"
                              onClick={() => setActionTarget({ kind: 'stop', row })}
                            >
                              <i className="fa fa-pause me-1" />
                              Stop
                            </button>
                          )}
                        </div>
                      )
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        show={actionTarget !== null}
        title={actionTarget?.kind === 'run' ? 'Run Integration?' : 'Stop Integration?'}
        message={
          actionTarget
            ? `${actionTarget.kind === 'run' ? 'Run' : 'Stop'} market data integration for tenant ${actionTarget.row.tenantName}?`
            : ''
        }
        confirmLabel={actionTarget?.kind === 'run' ? 'Run' : 'Stop'}
        confirmVariant={actionTarget?.kind === 'run' ? 'primary' : 'danger'}
        busy={runMut.isPending || stopMut.isPending}
        onConfirm={confirmAction}
        onCancel={() => setActionTarget(null)}
      />
    </div>
  );
}
